import { useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { ActionPopover } from "@/components/action-popover";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { SearchSelect } from "@/components/search-select";
import { Button } from "@/components/ui/button";
import { formatApiErrorMessage } from "@/lib/api-error";
import { formatStatus } from "@/lib/utils";
import { useCreateAction } from "./hooks";
import type { PurchaseOrderLineItemRead } from "./types";
import { ComposeInput, ComposeTextarea } from "@/components/compose-field";

const ACTION_TYPES = [
  "other",
  "account_setup",
  "info_request",
  "delay",
  "backorder",
  "substitution",
  "discontinued",
  "price_change",
  "additional_charge",
  "payment_charge",
  "freight_policy_change",
  "freight_mode",
  "source_change",
] as const;

type Team = "operations" | "sales" | "accounting";

const TEAMS: ReadonlyArray<{ value: Team; label: string }> = [
  { value: "operations", label: "Ops" },
  { value: "sales", label: "Sales" },
  { value: "accounting", label: "Accounting" },
];

/** Account setup and payment charges are accounting's work (USA-1555); the rest start with Operations. */
function defaultTeam(actionType: string): Team {
  return actionType === "account_setup" || actionType === "payment_charge"
    ? "accounting"
    : "operations";
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

/**
 * Staff raise an action item on a PO by hand: a short popover beside the Actions heading, a bottom
 * sheet on phones, like every other quick action (USA-1555).
 */
export function AddActionPopover({
  poUuid,
  lineItems,
}: {
  poUuid: string;
  lineItems: PurchaseOrderLineItemRead[];
}) {
  const createAction = useCreateAction(poUuid);
  const [summary, setSummary] = useState("");
  const [details, setDetails] = useState("");
  const [actionType, setActionType] = useState<string>("other");
  const [team, setTeam] = useState<Team>("operations");
  const [teamPicked, setTeamPicked] = useState(false);
  const [lineItemId, setLineItemId] = useState<string | undefined>();
  const pending = createAction.isPending;

  const reset = () => {
    setSummary("");
    setDetails("");
    setActionType("other");
    setTeam("operations");
    setTeamPicked(false);
    setLineItemId(undefined);
  };

  return (
    <ActionPopover
      align="end"
      trigger={
        <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs">
          <Plus className="size-3.5" aria-hidden="true" />
          Add action
        </Button>
      }
      title="New action item"
      confirmLabel="Add action item"
      pending={pending}
      pendingLabel="Adding..."
      canConfirm={summary.trim().length > 0}
      onConfirm={(_note, close) =>
        createAction.mutate(
          {
            action_type: actionType,
            summary: summary.trim(),
            details: details.trim() || null,
            assigned_role: team,
            line_item_id: lineItemId ? Number(lineItemId) : null,
          },
          {
            onSuccess: () => {
              toast.success("Action item added");
              reset();
              close();
            },
            onError: (error) =>
              toast.error(formatApiErrorMessage("Action item not added", error)),
          },
        )
      }
    >
      <ComposeInput
        aria-label="Summary"
        placeholder="What needs doing?"
        value={summary}
        onChange={(event) => setSummary(event.target.value)}
        maxLength={500}
        disabled={pending}
        className="h-9 text-sm"
      />
      <ComposeTextarea
        aria-label="Details"
        placeholder="Details (optional)"
        value={details}
        onChange={(event) => setDetails(event.target.value)}
        maxLength={4000}
        disabled={pending}
        className="min-h-16 text-sm"
      />
      <div className="space-y-2 pt-1">
        <Field label="Team">
          <ReviewChoiceGroup
            label="Team"
            value={team}
            options={TEAMS}
            disabled={pending}
            onChange={(next) => {
              setTeam(next);
              setTeamPicked(true);
            }}
            optionClassName="min-h-7 px-2"
          />
        </Field>
        <Field label="Type">
          <SearchSelect
            ariaLabel="Type"
            title="Action type"
            className="h-8 w-full text-sm"
            value={actionType}
            options={ACTION_TYPES.map((type) => ({ value: type, label: formatStatus(type) }))}
            onChange={(next) => {
              if (!next) return;
              setActionType(next);
              if (!teamPicked) setTeam(defaultTeam(next));
            }}
            searchPlaceholder="Search types"
            disabled={pending}
          />
        </Field>
        {lineItems.length > 0 && (
          <Field label="Item">
            <SearchSelect
              ariaLabel="Line item"
              title="Line item"
              className="h-8 w-full text-sm"
              value={lineItemId}
              allLabel="Whole PO"
              options={lineItems.map((lineItem) => ({
                value: String(lineItem.id),
                label: `#${lineItem.item_number} ${lineItem.model}`,
                description: lineItem.description,
              }))}
              onChange={setLineItemId}
              searchPlaceholder="Search line items"
              disabled={pending}
            />
          </Field>
        )}
      </div>
    </ActionPopover>
  );
}
